import {
  JobCountResponse,
  SavedSearchResponse,
} from '@doda/api-client/aspida/@types';
import { OP_SEARCH_ITEMS } from '@doda/common/src/constants/searchItems';
import {
  FeaturedSearchConditionQuery,
  GetInitialIdListArgument,
} from 'components/SearchSideBar/types';
import { PickUpConditionListType } from 'types/pickUpConditions';

// 新着のチェックボックスの初期状態を返す
const getNewArrivalIsChecked = ({
  savedSearchCondition,
  featuredSearchCondition,
  queries,
}: GetInitialIdListArgument) => {
  // 保存した検索条件
  if (savedSearchCondition) {
    return savedSearchCondition.isNewArrival ?? false;
  }

  // 特集枠検索条件
  if (featuredSearchCondition) {
    return featuredSearchCondition.isNewArrival ?? false;
  }

  // クエリから取得した検索条件
  if (queries) {
    return queries.some((query) => query === OP_SEARCH_ITEMS.newArrival.id);
  }

  return false;
};

// 締め切り間近のチェックボックスの初期状態を返す
const getNearDeadlineIsChecked = ({
  savedSearchCondition,
  featuredSearchCondition,
  queries,
}: GetInitialIdListArgument) => {
  // 保存した検索条件
  if (savedSearchCondition) {
    return savedSearchCondition.isNearDeadline ?? false;
  }

  // 特集枠検索条件
  if (featuredSearchCondition) {
    return featuredSearchCondition.isNearDeadline ?? false;
  }

  // クエリから取得した検索条件
  if (queries) {
    return queries.some((query) => query === OP_SEARCH_ITEMS.deadline.id);
  }

  return false;
};

/**
 * 新着の初期状態を生成
 * @param jobCount 求人件数
 * @param opQuery クエリパラメータ
 * @param savedSearchCondition 保存した検索条件のレスポンス
 * @param featuredSearchCondition 特集枠検索条件のレスポンス
 * @returns 新着の初期状態
 */
export const createInitialIsNewArrivalData = (
  jobCount: JobCountResponse['newArrivalCount'],
  opQuery: string[] | undefined,
  savedSearchCondition?: SavedSearchResponse | undefined,
  featuredSearchCondition?: FeaturedSearchConditionQuery | undefined
) => {
  const queries = opQuery ?? [];

  const isChecked = getNewArrivalIsChecked({
    savedSearchCondition,
    featuredSearchCondition,
    queries,
  });

  const initialData: PickUpConditionListType = {
    id: OP_SEARCH_ITEMS.newArrival.id,
    name: OP_SEARCH_ITEMS.newArrival.name,
    isChecked,
    jobCount: jobCount ?? 0,
    type: 'newArrival',
  };

  return initialData;
};

/**
 * 締め切り間近の初期状態を生成
 * @param jobCount 求人件数
 * @param opQuery クエリパラメータ
 * @param savedSearchCondition 保存した検索条件のレスポンス
 * @param featuredSearchCondition 特集枠検索条件のレスポンス
 * @returns 締め切り間近の初期状態
 */
export const createInitialIsNearDeadline = (
  jobCount: JobCountResponse['nearDeadlineCount'],
  opQuery: string[] | undefined,
  savedSearchCondition?: SavedSearchResponse | undefined,
  featuredSearchCondition?: FeaturedSearchConditionQuery | undefined
) => {
  const queries = opQuery ?? [];

  const isChecked = getNearDeadlineIsChecked({
    savedSearchCondition,
    featuredSearchCondition,
    queries,
  });

  const initialData: PickUpConditionListType = {
    id: OP_SEARCH_ITEMS.deadline.id,
    name: OP_SEARCH_ITEMS.deadline.name,
    isChecked,
    jobCount: jobCount ?? 0,
    type: 'nearDeadline',
  };

  return initialData;
};
